'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Input,
  Stack,
  Typography,
} from '@org/design-system';
import type { AuthConfig } from '../types/app-config';
import { useRuntimeAuth } from './auth-context';
import { getLoginPath } from './auth-paths';
import { useTranslator } from '../i18n/locale-context';

interface LoginPageLocationState {
  from?: string;
}

export interface LoginPageProps {
  auth?: AuthConfig;
}

export function LoginPage({ auth }: LoginPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading } = useRuntimeAuth();

  const locationState = location.state as LoginPageLocationState | null;
  const loginPath = getLoginPath(auth);
  const nextPath = useMemo(() => {
    const fallback = '/';
    const sourcePath = locationState?.from || fallback;
    if (!sourcePath.startsWith('/') || sourcePath === loginPath) {
      return fallback;
    }
    return sourcePath;
  }, [locationState?.from, loginPath]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(nextPath, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, nextPath]);

  const hasLoginEndpoint = !!auth?.loginEndpoint;

  if (hasLoginEndpoint) {
    return (
      <CredentialLoginForm
        auth={auth!}
        nextPath={nextPath}
        login={login}
        navigate={navigate}
      />
    );
  }

  return (
    <TokenLoginForm
      auth={auth}
      nextPath={nextPath}
      login={login}
      navigate={navigate}
    />
  );
}

// =============================================================================
// Email + Password Login (when auth.loginEndpoint is set)
// =============================================================================

function CredentialLoginForm({
  auth,
  nextPath,
  login,
  navigate,
}: {
  auth: AuthConfig;
  nextPath: string;
  login: (token: string) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const t = useTranslator();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail) {
      setError(t('auth.emailRequired'));
      return;
    }
    if (!trimmedPassword) {
      setError(t('auth.passwordRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(auth.loginEndpoint!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword }),
      });

      const body = await response.json() as {
        success?: boolean;
        data?: {
          tokens?: { accessToken?: string };
          token?: string;
          accessToken?: string;
        };
        error?: { message?: string };
        token?: string;
        accessToken?: string;
      };

      if (!response.ok || body.success === false) {
        const message = body.error?.message || t('auth.invalidCredentials');
        setError(message);
        return;
      }

      // Extract token from various response shapes
      const token =
        body.data?.tokens?.accessToken ||
        body.data?.token ||
        body.data?.accessToken ||
        body.token ||
        body.accessToken;

      if (!token) {
        setError(t('auth.loginFailed'));
        return;
      }

      login(token);
      navigate(nextPath, { replace: true });
    } catch {
      setError(t('auth.loginFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-6">
        <form onSubmit={handleSubmit}>
          <Stack gap={4}>
            <div className="space-y-1">
              <Typography variant="h3">{t('auth.signIn')}</Typography>
              <Typography variant="small" color="muted">
                {t('auth.enterCredentials')}
              </Typography>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="runtime-auth-email"
                className="text-sm font-medium text-foreground"
              >
                {t('auth.email')}
              </label>
              <Input
                id="runtime-auth-email"
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('auth.emailPlaceholder')}
                hasError={!!error}
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="runtime-auth-password"
                className="text-sm font-medium text-foreground"
              >
                {t('auth.password')}
              </label>
              <Input
                id="runtime-auth-password"
                type="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('auth.passwordPlaceholder')}
                hasError={!!error}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <Typography variant="small" className="text-destructive">
                {error}
              </Typography>
            )}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('auth.signingIn') : t('auth.continue')}
            </Button>

            {auth.registerEndpoint && (
              <Typography variant="small" className="text-center text-muted-foreground">
                {t('auth.createAccount')}
              </Typography>
            )}
          </Stack>
        </form>
      </Card>
    </div>
  );
}

// =============================================================================
// Token Paste Login (original behavior, no loginEndpoint)
// =============================================================================

function TokenLoginForm({
  nextPath,
  login,
  navigate,
}: {
  auth?: AuthConfig;
  nextPath: string;
  login: (token: string) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const t = useTranslator();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = token.trim();
    if (!normalized) {
      setError(t('auth.tokenRequired'));
      return;
    }

    login(normalized);
    navigate(nextPath, { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-6">
        <form onSubmit={handleSubmit}>
          <Stack gap={4}>
            <div className="space-y-1">
              <Typography variant="h3">{t('auth.signIn')}</Typography>
              <Typography variant="small" color="muted">
                {t('auth.enterToken')}
              </Typography>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="runtime-auth-token"
                className="text-sm font-medium text-foreground"
              >
                {t('auth.accessToken')}
              </label>
              <Input
                id="runtime-auth-token"
                type="password"
                value={token}
                onChange={e => {
                  setToken(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('auth.pasteToken')}
                hasError={!!error}
                autoComplete="off"
              />
              {error && (
                <Typography variant="small" className="text-destructive">
                  {error}
                </Typography>
              )}
            </div>

            <Button type="submit">{t('auth.continue')}</Button>
          </Stack>
        </form>
      </Card>
    </div>
  );
}
